import { useMemo, useState } from 'react';
import {
  Box,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Alert,
  InputAdornment,
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { ValidationSeverity, type MessageEvent } from '@k-ucap/shared';
import { getSeverityColor } from './utils.js';

export interface MessageEventBrowserProps {
  events: MessageEvent[];
  selectedEvent: MessageEvent | null;
  onSelect: (event: MessageEvent) => void;
  messageTypes: string[];
  sourceRoles: string[];
  targetRoles: string[];
  directions: string[];
  filter: {
    search: string;
    messageType: string;
    direction: string;
    validationStatus: string;
    sourceRole: string;
    targetRole: string;
  };
  onFilterChange: (patch: Partial<MessageEventBrowserProps['filter']>) => void;
}

export const MessageEventBrowser = ({
  events,
  selectedEvent,
  onSelect,
  messageTypes,
  sourceRoles,
  targetRoles,
  directions,
  filter,
  onFilterChange,
}: MessageEventBrowserProps) => {
  const [localSearch, setLocalSearch] = useState(filter.search);

  const handleSearchChange = (value: string) => {
    setLocalSearch(value);
    onFilterChange({ search: value });
  };

  const statusOptions = useMemo(
    () => [
      { value: '', label: '전체' },
      { value: ValidationSeverity.PASS, label: 'PASS' },
      { value: ValidationSeverity.WARN, label: 'WARN' },
      { value: ValidationSeverity.FAIL, label: 'FAIL' },
      { value: ValidationSeverity.UNKNOWN, label: 'UNKNOWN' },
      { value: ValidationSeverity.UNSUPPORTED, label: 'UNSUPPORTED' },
    ],
    []
  );

  return (
    <Paper sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant="h6" gutterBottom>
          Message Event Browser
        </Typography>
        <TextField
          fullWidth
          size="small"
          placeholder="Search ID, Name, Role..."
          value={localSearch}
          onChange={(e) => handleSearchChange(e.target.value)}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            },
          }}
          sx={{ mb: 1 }}
        />
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: 80, flex: 1 }}>
            <InputLabel>Type</InputLabel>
            <Select
              value={filter.messageType}
              label="Type"
              onChange={(e) => onFilterChange({ messageType: e.target.value })}
            >
              <MenuItem value="">전체</MenuItem>
              {messageTypes.map((t) => (
                <MenuItem key={t} value={t}>
                  {t}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 80, flex: 1 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={filter.validationStatus}
              label="Status"
              onChange={(e) => onFilterChange({ validationStatus: e.target.value })}
            >
              {statusOptions.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 80, flex: 1 }}>
            <InputLabel>Dir</InputLabel>
            <Select
              value={filter.direction}
              label="Dir"
              onChange={(e) => onFilterChange({ direction: e.target.value })}
            >
              <MenuItem value="">전체</MenuItem>
              {directions.map((d) => (
                <MenuItem key={d} value={d}>
                  {d}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
          <FormControl size="small" sx={{ minWidth: 100, flex: 1 }}>
            <InputLabel>Source</InputLabel>
            <Select
              value={filter.sourceRole}
              label="Source"
              onChange={(e) => onFilterChange({ sourceRole: e.target.value })}
            >
              <MenuItem value="">전체</MenuItem>
              {sourceRoles.map((r) => (
                <MenuItem key={r} value={r}>
                  {r}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 100, flex: 1 }}>
            <InputLabel>Target</InputLabel>
            <Select
              value={filter.targetRole}
              label="Target"
              onChange={(e) => onFilterChange({ targetRole: e.target.value })}
            >
              <MenuItem value="">전체</MenuItem>
              {targetRoles.map((r) => (
                <MenuItem key={r} value={r}>
                  {r}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>
      <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
        {events.length === 0 && (
          <Alert severity="info" sx={{ m: 2 }}>
            현재 프로젝트에 메시지 이벤트가 없습니다.
          </Alert>
        )}
        <List dense disablePadding>
          {events.map((event) => (
            <ListItem key={event.id} disablePadding>
              <ListItemButton
                selected={selectedEvent?.id === event.id}
                onClick={() => onSelect(event)}
                sx={{ borderBottom: 1, borderColor: 'divider' }}
              >
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Typography variant="body2" component="span" sx={{ fontWeight: 'bold' }}>
                        #{event.messageType}
                      </Typography>
                      <Typography variant="body2" component="span" noWrap sx={{ maxWidth: 140 }}>
                        {event.name}
                      </Typography>
                      <Chip
                        label={event.validation || 'N/A'}
                        size="small"
                        color={getSeverityColor(event.validation || '')}
                      />
                    </Box>
                  }
                  secondary={
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 0.5 }}>
                      <Typography variant="caption" component="span" color="text.secondary">
                        {new Date(event.timestamp).toLocaleTimeString()}
                      </Typography>
                      <Typography variant="caption" component="span" color="text.secondary">
                        {event.sourceRole || event.source} → {event.targetRole || event.target}
                      </Typography>
                      <Typography variant="caption" component="span" color="text.secondary">
                        Seq {event.sequenceNumber ?? '-'}
                      </Typography>
                    </Box>
                  }
                  slotProps={{ secondary: { component: 'div' } }}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Box>
      <Box sx={{ p: 1, borderTop: 1, borderColor: 'divider', textAlign: 'right' }}>
        <Typography variant="caption" color="text.secondary">
          {events.length} events
        </Typography>
      </Box>
    </Paper>
  );
};
